import { Component, ViewChild } from '@angular/core';
import { ButtonComponent } from 'components/input/button/button.component';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { DateInputComponent } from 'components/input/date-input/date-input.component';
import { CommunicationLogComponent } from 'components/task/taskflow/communication-log.component';
import { StorageContainer } from 'constants/StorageContainer';
import { CredentialType } from 'enums/CredentialType';
import { InputType } from 'enums/InputType';
import { SettlementStatus } from 'enums/SettlementStatus';
import { AccountingPlansLedgerPostsAmounts } from 'models/AccountingPlansLedgerPostsAmounts';
import { CommunicationLogEntryModel } from 'models/generated/Communication.Api.Models.CommunicationLogEntry';
import { SettlementModel } from 'models/generated/Entities.GormCrm.Settlement';
import { SettlementPaymentModel } from 'models/generated/Entities.GormCrm.SettlementPayment';
import { AccountingPlanDtoModel, SettlementPdfDtoModel } from 'models/generated/Models.SettlementPdfDto';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { CurrencyPipe } from 'pipes/currency.pipe';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { AccountingService } from 'services/accounting.service';
import { CommunicationService } from 'services/communication.service';
import { CredentialRequired } from 'services/credential.service';
import { FileStorageService } from 'services/file-storage.service';
import { ComponentUrl, NavigationService, UrlParam } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { ProductPlanService } from 'services/productplan.service';
import { PushService } from 'services/push.service';
import { LegalCaseService } from 'services/legal-case.service';
import { SettlementSatusComponent } from './settlement-status.component';
import { SettlementComponent } from './settlement.component';

@Component({
	selector: 'settlements-list',
	templateUrl: './settlements-list.component.html',
	imports: [Layout, CustomInputComponent, ListView, SettlementSatusComponent, SettlementComponent, DateInputComponent, CommunicationLogComponent, CurrencyPipe, TranslatePipe, CustomDatePipe]
})
@ComponentUrl('settlements-list')
@CredentialRequired(CredentialType.EditSettlements)
export class SettlementsListComponent {

	@ViewChild('approveButton') approveButton?: ButtonComponent;
	@ViewChild('reminderButton') reminderButton?: ButtonComponent;
	@ViewChild('paymentButton') paymentButton?: ButtonComponent;
	@ViewChild('acceptButton') acceptButton?: ButtonComponent;

	settlementStatus = SettlementStatus;

	private _settlementId?: string;
	@UrlParam
	set settlementId(id: string | undefined) {
		this._settlementId = id;
	}
	get settlementId() {
		return this.selectedSettlement?.id ?? this._settlementId;
	}

	settlements?: SettlementModel[];
	selectedSettlement?: SettlementModel;
	inputTypeEnum = InputType;
	newPaymentAmount = 0;
	newPaymentDate = new Date();
	approveSettlementCredential = CredentialType.ApproveSettlement;
	onSettlementChanges: Subscription;
	plansAmount = new AccountingPlansLedgerPostsAmounts();
	loading = false;
	onCommunicationLogChanges: Subscription;
	reminderSent = false;
	communicationLog: CommunicationLogEntryModel[] = [];
	showInactiveSettlements = false;

	constructor(private readonly legalCaseService: LegalCaseService,
		private readonly navigation: NavigationService,
		private readonly pushService: PushService,
		private readonly notificationService: NotificationService,
		private readonly accountingService: AccountingService,
		private readonly communicationService: CommunicationService,
		private readonly productPlanService: ProductPlanService,
		private readonly fileStorageService: FileStorageService) {

		this.loadSettlements();

		this.onSettlementChanges = this.pushService.onSettlementChanges.subscribe(event => {
			if (event.data.settlementId === this.selectedSettlement?.id) {
				this.loadSettlements();
			}
		});
		this.onCommunicationLogChanges = this.pushService.onCommunicationLogEntryChanged.subscribe(event => {
			if (this.selectedSettlement && event.data.companyId === this.selectedSettlement?.companyId) {
				this.communicationService.getLogEntries(this.selectedSettlement.companyId!).then(logs => {
					this.communicationLog = logs;
				})
			}
		});
	}

	getStatusColor(settlement: SettlementModel) {
		if ((this.legalCaseService.clientBehindPayment(settlement) || this.legalCaseService.clientAcceptanceOverdue(settlement)) && settlement.status !== SettlementStatus.Cancelled) return 'color-red-50';
		switch (settlement.status) {
			case SettlementStatus.Unconfirmed: return 'color-yellow-50';
			case SettlementStatus.Sent:
				return settlement.acceptedByClient ? 'color-mint-70' : 'color-mint-60';
			case SettlementStatus.Completed:
			case SettlementStatus.Cancelled: return 'color-grey-60';
			case SettlementStatus.Breach: return 'color-red-50';
			default: return 'color-black';
		}
	}

	getStatusTitle(settlement: SettlementModel) {
		if (settlement.status !== SettlementStatus.Cancelled) {
			if (this.legalCaseService.clientBehindPayment(settlement)) return 'settlements-list.settlement-status.client-behind-payment';
			if (this.legalCaseService.clientAcceptanceOverdue(settlement)) return 'settlements-list.settlement-status.client-acceptance-overdue';
		}
		let languageVariable = 'settlements-list.settlement-status.' + SettlementStatus[settlement.status]
		if (settlement.status == SettlementStatus.Sent && settlement.acceptedByClient)
			languageVariable += '-and-accepted';
		return languageVariable;
	}

	loadSettlements() {
		this.legalCaseService.getSettlements().then(settlements => {
			this.settlements = settlements;
			let selectedSettlement = this.settlements?.find(s => s.id === this.settlementId);
			if (selectedSettlement) this.selectSettlement(selectedSettlement);
		});
	}

	acceptSettlement() {
		this.legalCaseService.acceptSettlement(this.selectedSettlement!.id!);
	}

	closeSettlement() {
		this.notificationService.confirm('settlement.confirm-close').subscribe(result => {
			if (result) {
				this.legalCaseService.closeSettlement(this.selectedSettlement!.id!);
			}
		});
	}

	cancelSettlement() {
		let languageVariable = (this.selectedSettlement?.paymentSumInclVat! > 0) ? 'settlement.confirm-cancel' : 'settlement.confirm-cancel-with-payments';
		this.notificationService.confirm(languageVariable).subscribe(result => {
			if (result) {
				this.legalCaseService.cancelSettlement(this.selectedSettlement!.id!);
			}
		});
	}

	downloadFile(settlement: SettlementModel) {
		this.legalCaseService.getPdfUrl(settlement.id!).then(filePath => {
			this.fileStorageService.downloadFile(filePath, StorageContainer.contractAttachments);
		});
	}
	createPayment() {
		const payment = new SettlementPaymentModel();
		payment.created = this.newPaymentDate;
		payment.settlementId = this.selectedSettlement!.id;
		payment.amount = this.newPaymentAmount;

		this.legalCaseService.createPayment(payment).then(() => {
			this.newPaymentAmount = 0;
			this.newPaymentDate = new Date();
			this.paymentButton?.reset();
		}).catch(() => {
			this.paymentButton?.reset();
		});
	}

	selectSettlement(settlement: SettlementModel) {
		this.loading = true;
		this.selectedSettlement = settlement;
		this.reminderSent = false;
		let productPlanIds = settlement.settlementProductPlans.map(spp => spp.productPlanId!);
		this.plansAmount = new AccountingPlansLedgerPostsAmounts();

		let promises = [];

		promises.push(this.accountingService.getPlansOpenAndClosedAmounts(productPlanIds).then(amounts => {
			this.plansAmount = amounts;
		}));

		promises.push(this.communicationService.getLogEntries(this.selectedSettlement.companyId!).then(logs => {
			this.communicationLog = logs;
		}));

		Promise.all(promises).then(() => this.loading = false);

		this.navigation.refreshUrl();
		if (this.approveButton) this.approveButton.reset();
		if (this.acceptButton) this.acceptButton.reset();
	}

	sendReminder(settlementId: string) {
		this.legalCaseService.sendReminder(settlementId).then(_ => {
			this.reminderButton!.reset();
			this.reminderSent = true;
		});
	}

	approveSettlement(settlement: SettlementModel) {
		let pdf = new SettlementPdfDtoModel();
		pdf.company = settlement.company;
		let promises = [];

		promises.push(
			this.productPlanService.getAllProductPlansAndAccountingPlansFromCompany(settlement.companyId!).then((productPlans) => {
				pdf.productPlans = productPlans.filter(pp => settlement.settlementProductPlans.some(spp => spp.productPlanId == pp.id));
				pdf.accountingPlans = pdf.productPlans.map(pp => this.accountingService.filterActiveAccountingPlan(pp.accountingPlans)).map(ap => {
					let planDto = new AccountingPlanDtoModel();
					planDto.accountingPlanId = ap.id;
					planDto.productPlanId = ap.productPlanId;
					planDto.startDate = ap.startDate;
					planDto.endDate = ap.endDate;
					planDto.totalPlanValue = ap.totalPlanValue;
					return planDto;
				});

			})
		);
		Promise.all(promises).then(() => {
			pdf.accountingPlans.forEach(ap => {
				ap.invoicedValue = this.plansAmount.closedAmount(ap.accountingPlanId!);
			});
			this.legalCaseService.approveSettlement(settlement.id!, pdf).then(() => {
				if (this.approveButton) this.approveButton.succeeded();
			});
		});
	}

	ngOnDestroy() {
		this.onSettlementChanges.unsubscribe();
	}

}
